import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  TrendingUp, Plus, Trash2, Sparkles, RefreshCw, Loader2,
  Clock, Star, ExternalLink, Tag, Zap, Search, Filter,
  BarChart3, ArrowRight, CheckCircle2, Pencil, Globe, Link2, FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceLabels = {
  google_trends: "Google Trends",
  twitter: "Twitter/X",
  reddit: "Reddit",
  manual: "Reczny",
  ai_suggested: "AI",
};

const sourceColors = {
  google_trends: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  twitter: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
  reddit: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  manual: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  ai_suggested: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
};

function ScoreBar({ score }) {
  const color = score >= 80 ? "text-emerald-500" : score >= 50 ? "text-amber-500" : "text-slate-400";
  return (
    <div className="flex items-center gap-2">
      <Progress value={score} className="h-1.5 flex-1" />
      <span className={`text-xs font-bold ${color}`}>{score}</span>
    </div>
  );
}

export default function TrendingTopics() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [trends, setTrends] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [scoringId, setScoringId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [suggestingId, setSuggestingId] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [siteSuggestion, setSiteSuggestion] = useState(null);
  const [form, setForm] = useState({
    title: "", topic: "", category: "Technologia", score: 50,
    source: "manual", keywords: "", expires_hours: 48, description: "",
  });
  const [editForm, setEditForm] = useState({
    title: "", description: "", category: "", keywords: "", source_urls: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== "all") params.set("category", filterCategory);
      if (filterSource !== "all") params.set("source", filterSource);

      const [trendsRes, statsRes, catsRes] = await Promise.all([
        axios.get(`${API}/trends?${params.toString()}`),
        axios.get(`${API}/trends/stats`),
        axios.get(`${API}/trends/categories`),
      ]);
      setTrends(trendsRes.data);
      setStats(statsRes.data);
      setCategories(catsRes.data.categories || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterCategory, filterSource]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFetch = async () => {
    setFetching(true);
    try {
      const res = await axios.post(`${API}/trends/fetch`, {
        category: filterCategory !== "all" ? filterCategory : null,
        ai_model: "gemini"
      });
      toast.success(`${t("trends.fetchComplete")}: ${res.data.fetched} ${t("trends.topicsFetched")}, ${res.data.saved} ${t("trends.saved")}`);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setFetching(false); }
  };

  const handleScore = async (trendId) => {
    setScoringId(trendId);
    try {
      await axios.post(`${API}/trends/${trendId}/score`, { ai_model: "gemini" });
      toast.success(t("trends.scored"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setScoringId(null); }
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/trends`, {
        ...form,
        keywords: form.keywords ? form.keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
      });
      toast.success(t("common.success"));
      setDialogOpen(false);
      setForm({ title: "", topic: "", category: "Technologia", score: 50, source: "manual", keywords: "", expires_hours: 48, description: "" });
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/trends/${id}`);
      toast.success(t("common.success"));
      if (selectedTrend?.id === id) setSelectedTrend(null);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  // Otwórz dialog edycji
  const handleEditOpen = (trend) => {
    setEditForm({
      title: trend.title || "",
      description: trend.description || "",
      category: trend.category || "Technologia",
      keywords: (trend.keywords || []).join(", "),
      source_urls: (trend.source_urls || []).join("\n"),
    });
    setEditDialogOpen(true);
  };

  // Zapisz edycję
  const handleEditSave = async () => {
    if (!selectedTrend) return;
    try {
      const keywords = editForm.keywords.split(",").map(k => k.trim()).filter(k => k);
      const source_urls = editForm.source_urls.split("\n").map(u => u.trim()).filter(u => u);
      
      await axios.put(`${API}/trends/${selectedTrend.id}`, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        keywords,
        source_urls,
      });
      
      toast.success("Temat zaktualizowany");
      setEditDialogOpen(false);
      
      // Odśwież dane
      const res = await axios.get(`${API}/trends/${selectedTrend.id}`);
      setSelectedTrend(res.data);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  // AI sugestia strony
  const handleSuggestSite = async (trendId) => {
    setSuggestingId(trendId);
    setSiteSuggestion(null);
    try {
      const res = await axios.post(`${API}/trends/${trendId}/suggest-site`);
      setSiteSuggestion(res.data);
      toast.success("AI dobrał stronę i kategorię");
    } catch (err) { toast.error("Błąd sugestii AI"); }
    finally { setSuggestingId(null); }
  };

  // Wzbogać o źródła
  const handleEnrichSources = async (trendId) => {
    setEnrichingId(trendId);
    try {
      const res = await axios.post(`${API}/trends/${trendId}/enrich`);
      toast.success(`Pobrano ${res.data.sources_count} źródeł`);
      // Odśwież trend
      const updated = await axios.get(`${API}/trends/${trendId}`);
      setSelectedTrend(updated.data);
    } catch (err) { toast.error("Błąd pobierania źródeł"); }
    finally { setEnrichingId(null); }
  };

  // Ulepszone przekazywanie do generatora
  const handleUseForArticle = async (trend) => {
    await axios.patch(`${API}/trends/${trend.id}/used`);
    
    // Przygotuj dane do przekazania
    const trendData = {
      title: trend.title,
      description: trend.description || "",
      category: trend.category || "",
      keywords: (trend.keywords || []).join(", "),
      source_urls: (trend.source_urls || []).join("\n"),
      enriched_sources: trend.enriched_sources || [],
      ai_suggested_site_id: trend.ai_suggested_site_id || siteSuggestion?.suggested_site_id || "",
      ai_suggested_category: trend.ai_suggested_category || siteSuggestion?.suggested_category || "",
    };
    
    // Zakoduj i przekaż przez URL
    const encodedData = encodeURIComponent(JSON.stringify(trendData));
    navigate(`/generator?trendData=${encodedData}`);
  };

  const isExpired = (trend) => {
    return new Date(trend.expires_at) < new Date();
  };

  const timeLeft = (trend) => {
    const diff = new Date(trend.expires_at) - new Date();
    if (diff <= 0) return t("trends.expired");
    const hours = Math.floor(diff / 3600000);
    if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h`;
  };

  return (
    <div data-testid="trends-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("trends.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("trends.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-trend-btn" className="gap-2 rounded-lg">
                <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("trends.addManual")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{t("trends.addManual")}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3 py-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("trends.topicTitle")}</Label>
                  <Input data-testid="trend-title-input" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("trends.topicDesc")}</Label>
                  <Textarea value={form.description} rows={2} className="resize-none"
                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("trends.category")}</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("trends.expiresIn")}: {form.expires_hours}h</Label>
                    <Slider value={[form.expires_hours]} onValueChange={([v]) => setForm({ ...form, expires_hours: v })}
                      min={6} max={168} step={6} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("trends.keywords")}</Label>
                  <Input value={form.keywords} placeholder="SEO, trend, ..."
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("sites.cancel")}</Button>
                <Button data-testid="save-trend-btn" onClick={handleAdd}>{t("sites.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button data-testid="fetch-trends-btn" onClick={handleFetch} disabled={fetching} className="gap-2 rounded-lg">
            {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" strokeWidth={1.5} />}
            {fetching ? t("trends.fetching") : t("trends.fetchTrends")}
          </Button>
        </div>
      </div>

      {/* Statystyki */}
      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold font-heading text-foreground">{stats.active}</p>
              <p className="text-[10px] text-muted-foreground">{t("trends.activeTrends")}</p>
            </CardContent>
          </Card>
          {Object.entries(stats.by_source || {}).filter(([, v]) => v > 0).map(([key, val]) => (
            <Card key={key} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold font-heading text-foreground">{val}</p>
                <p className="text-[10px] text-muted-foreground">{sourceLabels[key] || key}</p>
              </CardContent>
            </Card>
          ))}
          {stats.expired > 0 && (
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold font-heading text-muted-foreground">{stats.expired}</p>
                <p className="text-[10px] text-muted-foreground">{t("trends.expired")}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filtry */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="category-filter" className="h-8 text-xs w-[150px]">
            <SelectValue placeholder={t("trends.allCategories")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t("trends.allCategories")}</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger data-testid="source-filter" className="h-8 text-xs w-[140px]">
            <SelectValue placeholder={t("trends.allSources")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{t("trends.allSources")}</SelectItem>
            {Object.entries(sourceLabels).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista trendow */}
        <div className={selectedTrend ? "lg:col-span-1" : "lg:col-span-3"}>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {trends.length === 0 ? (
                <div data-testid="no-trends" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <TrendingUp className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("trends.noTrends")}</p>
                  <Button variant="outline" size="sm" className="mt-3 text-xs" onClick={handleFetch}>
                    {t("trends.fetchTrends")}
                  </Button>
                </div>
              ) : (
                <ScrollArea className="max-h-[600px]">
                  <div className="divide-y divide-border">
                    {trends.map((trend) => {
                      const expired = isExpired(trend);
                      return (
                        <button key={trend.id} data-testid={`trend-${trend.id}`}
                          onClick={() => setSelectedTrend(trend)}
                          className={`w-full flex items-start gap-3 p-4 text-left transition-all hover:bg-accent/30 ${
                            selectedTrend?.id === trend.id ? "bg-primary/5" : ""
                          } ${expired ? "opacity-50" : ""}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground leading-snug">{trend.title}</p>
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              <Badge className={`text-[9px] border-0 rounded-md ${sourceColors[trend.source] || sourceColors.manual}`}>
                                {sourceLabels[trend.source] || trend.source}
                              </Badge>
                              <Badge variant="outline" className="text-[9px] rounded-md">{trend.category}</Badge>
                              {trend.used_for_article && (
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[9px] border-0 rounded-md">
                                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> {t("trends.used")}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`text-lg font-bold font-heading ${
                              trend.score >= 80 ? "text-emerald-500" : trend.score >= 50 ? "text-amber-500" : "text-slate-400"
                            }`}>{trend.score}</div>
                            <p className={`text-[9px] ${expired ? "text-red-400" : "text-muted-foreground"}`}>
                              <Clock className="w-2.5 h-2.5 inline mr-0.5" />{timeLeft(trend)}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail panel */}
        {selectedTrend && (
          <div className="lg:col-span-2">
            <Card data-testid="trend-detail" className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base font-heading text-foreground">{selectedTrend.title}</CardTitle>
                    {selectedTrend.description && (
                      <p className="text-xs text-muted-foreground mt-1">{selectedTrend.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0 ml-3">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => handleEditOpen(selectedTrend)}>
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                      onClick={() => handleDelete(selectedTrend.id)}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 pt-2 space-y-4">
                {/* Meta info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <Star className="w-4 h-4 text-amber-500 mb-1" strokeWidth={1.5} />
                    <p className="text-lg font-bold font-heading text-foreground">{selectedTrend.score}</p>
                    <p className="text-[10px] text-muted-foreground">Score</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <Tag className="w-4 h-4 text-muted-foreground mb-1" strokeWidth={1.5} />
                    <p className="text-xs font-semibold text-foreground">{selectedTrend.category}</p>
                    <p className="text-[10px] text-muted-foreground">{t("trends.category")}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <TrendingUp className="w-4 h-4 text-muted-foreground mb-1" strokeWidth={1.5} />
                    <p className="text-xs font-semibold text-foreground">{sourceLabels[selectedTrend.source]}</p>
                    <p className="text-[10px] text-muted-foreground">{t("trends.source")}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <Clock className="w-4 h-4 text-muted-foreground mb-1" strokeWidth={1.5} />
                    <p className={`text-xs font-semibold ${isExpired(selectedTrend) ? "text-red-500" : "text-foreground"}`}>
                      {timeLeft(selectedTrend)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{t("trends.timeLeft")}</p>
                  </div>
                </div>

                {/* Keywords */}
                {selectedTrend.keywords?.length > 0 && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">{t("trends.keywords")}</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedTrend.keywords.map((kw, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] rounded-md">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Scoring */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-foreground">{t("trends.aiScoring")}</p>
                    <Button size="sm" data-testid="score-trend-btn"
                      onClick={() => handleScore(selectedTrend.id)}
                      disabled={scoringId === selectedTrend.id}
                      className="h-7 text-xs gap-1.5 rounded-md">
                      {scoringId === selectedTrend.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                       <Sparkles className="w-3 h-3" strokeWidth={1.5} />}
                      {t("trends.scoreNow")}
                    </Button>
                  </div>

                  {selectedTrend.ai_scoring && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2 rounded-lg bg-muted/50 text-center">
                          <p className="text-sm font-bold text-foreground">{selectedTrend.ai_scoring.competition || "?"}</p>
                          <p className="text-[9px] text-muted-foreground">{t("trends.competition")}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/50 text-center">
                          <p className="text-sm font-bold text-foreground">{selectedTrend.ai_scoring.estimated_traffic || "?"}</p>
                          <p className="text-[9px] text-muted-foreground">{t("trends.traffic")}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/50 text-center">
                          <p className="text-sm font-bold text-foreground">{selectedTrend.ai_scoring.best_time_to_publish || "?"}</p>
                          <p className="text-[9px] text-muted-foreground">{t("trends.bestTime")}</p>
                        </div>
                      </div>
                      {selectedTrend.ai_scoring.suggested_titles?.length > 0 && (
                        <div className="p-3 rounded-lg bg-muted/50">
                          <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">{t("trends.altTitles")}</p>
                          {selectedTrend.ai_scoring.suggested_titles.map((t2, i) => (
                            <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-primary/40">{t2}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sugestia strony AI */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-foreground">Sugerowana strona i źródła</p>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline"
                        onClick={() => handleEnrichSources(selectedTrend.id)}
                        disabled={enrichingId === selectedTrend.id}
                        className="h-7 text-xs gap-1.5 rounded-md">
                        {enrichingId === selectedTrend.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                         <Link2 className="w-3 h-3" strokeWidth={1.5} />}
                        Pobierz źródła
                      </Button>
                      <Button size="sm"
                        onClick={() => handleSuggestSite(selectedTrend.id)}
                        disabled={suggestingId === selectedTrend.id}
                        className="h-7 text-xs gap-1.5 rounded-md">
                        {suggestingId === selectedTrend.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                         <Globe className="w-3 h-3" strokeWidth={1.5} />}
                        Dobierz stronę AI
                      </Button>
                    </div>
                  </div>

                  {/* Wyświetl sugestię strony */}
                  {(siteSuggestion || selectedTrend.ai_suggested_site_id) && (
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Sugerowana strona</span>
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {siteSuggestion?.suggested_site?.name || selectedTrend.ai_suggested_site_id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Kategoria: {siteSuggestion?.suggested_category || selectedTrend.ai_suggested_category}
                      </p>
                      {(siteSuggestion?.reasoning || selectedTrend.ai_suggestion_reasoning) && (
                        <p className="text-[10px] text-muted-foreground mt-1 italic">
                          {siteSuggestion?.reasoning || selectedTrend.ai_suggestion_reasoning}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Wyświetl źródła */}
                  {selectedTrend.enriched_sources?.length > 0 && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-2">
                        <FileText className="w-3 h-3 inline mr-1" />
                        Źródła ({selectedTrend.enriched_sources.length})
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedTrend.enriched_sources.map((src, i) => (
                          <div key={i} className="text-xs p-2 bg-background rounded border">
                            <a href={src.url} target="_blank" rel="noopener noreferrer"
                               className="text-primary hover:underline flex items-center gap-1">
                              <ExternalLink className="w-2.5 h-2.5" />
                              {src.title || src.url}
                            </a>
                            {src.content && (
                              <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{src.content.slice(0, 150)}...</p>
                            )}
                            {src.error && (
                              <p className="text-[10px] text-red-500 mt-1">Błąd: {src.error}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* URL źródłowe ręczne */}
                  {selectedTrend.source_urls?.length > 0 && !selectedTrend.enriched_sources?.length && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">
                        Linki źródłowe
                      </p>
                      {selectedTrend.source_urls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-primary hover:underline block truncate">
                          {url}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Akcja - generuj artykul */}
                <Button data-testid="generate-from-trend" onClick={() => handleUseForArticle(selectedTrend)}
                  className="gap-2 rounded-lg w-full h-10">
                  <Zap className="w-4 h-4" strokeWidth={1.5} />
                  {t("trends.generateArticle")}
                  <ArrowRight className="w-3.5 h-3.5 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Dialog edycji tematu */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edytuj temat</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Tytuł</Label>
              <Input value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Opis (o czym ma być artykuł)</Label>
              <Textarea value={editForm.description} rows={3} className="resize-none"
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Opisz szczegółowo o czym AI ma napisać artykuł..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kategoria</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Słowa kluczowe (oddzielone przecinkiem)</Label>
              <Input value={editForm.keywords}
                onChange={(e) => setEditForm({ ...editForm, keywords: e.target.value })}
                placeholder="słowo1, słowo2, słowo3" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Linki źródłowe (jeden na linię)</Label>
              <Textarea value={editForm.source_urls} rows={3} className="resize-none font-mono text-xs"
                onChange={(e) => setEditForm({ ...editForm, source_urls: e.target.value })}
                placeholder="https://example.com/artykul1&#10;https://example.com/artykul2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t("sites.cancel")}</Button>
            <Button onClick={handleEditSave}>{t("sites.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {stats?.last_fetch && (
        <p className="text-[10px] text-muted-foreground/50 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {t("trends.lastFetch")}: {new Date(stats.last_fetch).toLocaleString()}
        </p>
      )}
    </div>
  );
}
